import { closeModalById, openModalById } from "./floatingModal.js";

let map = null;
let marker = null;
let leafletLoaded = false;
let deliveryZonesCircles = [];
let lastCepData = null; // Guarda dados do último CEP pesquisado

/**
 * Carrega o Leaflet CSS e JS dinamicamente
 */
function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (leafletLoaded && window.L) {
      resolve(window.L);
      return;
    }

    // Carrega CSS
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      link.integrity = "sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=";
      link.crossOrigin = "";
      document.head.appendChild(link);
    }

    // Carrega CSS customizado para o marcador do CEP
    if (!document.querySelector("#cep-marker-styles")) {
      const style = document.createElement("style");
      style.id = "cep-marker-styles";
      style.textContent = `
        .cep-marker-container {
          background: transparent !important;
          border: none !important;
        }
        .cep-marker-icon {
          width: 16px;
          height: 16px;
          background-color: #7c3aed;
          border: 3px solid #fff;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
        }
      `;
      document.head.appendChild(style);
    }

    // Carrega JS
    if (!document.querySelector('script[src*="leaflet.js"]')) {
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.integrity = "sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=";
      script.crossOrigin = "";
      script.onload = () => {
        leafletLoaded = true;
        resolve(window.L);
      };
      script.onerror = () => reject(new Error("Falha ao carregar Leaflet"));
      document.body.appendChild(script);
    } else {
      // Script já existe, aguarda carregamento
      const checkLeaflet = setInterval(() => {
        if (window.L) {
          clearInterval(checkLeaflet);
          leafletLoaded = true;
          resolve(window.L);
        }
      }, 100);
    }
  });
}

/**
 * Valida formato do CEP brasileiro
 */
function isValidCep(cep) {
  const cleanCep = cep.replace(/\D/g, "");
  return cleanCep.length === 8;
}

/**
 * Busca dados do CEP via ViaCEP
 */
async function fetchCepData(cep) {
  const cleanCep = cep.replace(/\D/g, "");
  const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
  const data = await response.json();

  if (data.erro) {
    throw new Error("CEP não encontrado");
  }

  return data;
}

/**
 * Busca coordenadas via Nominatim (OpenStreetMap)
 */
async function fetchCoordinates(address, city, state) {
  const query = encodeURIComponent(`${address}, ${city}, ${state}, Brasil`);
  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1`,
  );
  const data = await response.json();

  if (data.length === 0) {
    // Fallback: busca só por cidade
    const fallbackQuery = encodeURIComponent(`${city}, ${state}, Brasil`);
    const fallbackResponse = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${fallbackQuery}&limit=1`,
    );
    const fallbackData = await fallbackResponse.json();

    if (fallbackData.length === 0) {
      throw new Error("Coordenadas não encontradas");
    }

    return {
      lat: parseFloat(fallbackData[0].lat),
      lon: parseFloat(fallbackData[0].lon),
    };
  }

  return {
    lat: parseFloat(data[0].lat),
    lon: parseFloat(data[0].lon),
  };
}

/**
 * Verifica se o CEP está em uma zona de entrega via AJAX
 */
async function checkDeliveryZone(cep, state) {
  return new Promise((resolve, reject) => {
    if (typeof window.jQuery === "undefined") {
      reject(new Error("jQuery não disponível"));
      return;
    }

    window.jQuery.ajax({
      url: window.jabuticabaAjax?.ajaxurl || "/wp-admin/admin-ajax.php",
      type: "POST",
      data: {
        action: "check_cep_delivery_zone",
        cep: cep.replace(/\D/g, ""),
        state: state,
      },
      success: function (response) {
        if (response.success) {
          resolve(response.data);
        } else {
          resolve({
            delivers: false,
            message: response.data?.message || "Erro ao verificar",
          });
        }
      },
      error: function () {
        reject(new Error("Erro de conexão"));
      },
    });
  });
}

/**
 * Salva e-mail de interessado via AJAX
 */
async function saveInterestedEmail(email) {
  return new Promise((resolve, reject) => {
    if (typeof window.jQuery === "undefined") {
      reject(new Error("jQuery não disponível"));
      return;
    }

    if (!lastCepData) {
      reject(new Error("Dados do CEP não disponíveis"));
      return;
    }

    window.jQuery.ajax({
      url: window.jabuticabaAjax?.ajaxurl || "/wp-admin/admin-ajax.php",
      type: "POST",
      data: {
        action: "save_cep_interested",
        email: email,
        cep: lastCepData.cep,
        neighborhood: lastCepData.bairro || "",
        city: lastCepData.localidade || "",
        state: lastCepData.uf || "",
      },
      success: function (response) {
        if (response.success) {
          resolve(response.data);
        } else {
          reject(new Error(response.data?.message || "Erro ao salvar"));
        }
      },
      error: function () {
        reject(new Error("Erro de conexão"));
      },
    });
  });
}

/**
 * Busca as zonas de entrega com coordenadas via AJAX
 */
async function fetchDeliveryZones() {
  return new Promise((resolve, reject) => {
    if (typeof window.jQuery === "undefined") {
      reject(new Error("jQuery não disponível"));
      return;
    }

    window.jQuery.ajax({
      url: window.jabuticabaAjax?.ajaxurl || "/wp-admin/admin-ajax.php",
      type: "POST",
      data: {
        action: "get_delivery_zones",
      },
      success: function (response) {
        if (response.success && response.data?.zones) {
          resolve(response.data.zones);
        } else {
          resolve([]);
        }
      },
      error: function () {
        reject(new Error("Erro ao buscar zonas de entrega"));
      },
    });
  });
}

/**
 * Inicializa ou atualiza o mapa com zonas de entrega
 */
async function initMap(L, cepLat, cepLon, address) {
  const mapContainer = document.getElementById("cep-map");
  const loadingContainer = document.getElementById("cep-map-loading");

  if (!mapContainer) return;

  // Destroi mapa existente se houver
  if (map) {
    map.remove();
    map = null;
    marker = null;
    deliveryZonesCircles = [];
  }

  // Busca as zonas de entrega
  let deliveryZones = [];
  try {
    deliveryZones = await fetchDeliveryZones();
  } catch (error) {
    console.warn("Não foi possível carregar zonas de entrega:", error);
  }

  // Calcula o centro do mapa baseado nas zonas ou no CEP
  let centerLat = cepLat;
  let centerLon = cepLon;

  if (deliveryZones.length > 0) {
    // Usa o centro da primeira zona como ponto inicial
    centerLat = deliveryZones[0].lat;
    centerLon = deliveryZones[0].lng;
  }

  // Cria novo mapa
  map = L.map("cep-map", {
    zoomControl: false,
    scrollWheelZoom: true,
  }).setView([centerLat, centerLon], 12);

  // Controle de zoom no canto inferior direito
  L.control
    .zoom({
      position: "bottomright",
    })
    .addTo(map);

  // Adiciona camada do OpenStreetMap
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(map);

  // Desenha círculos das zonas de entrega
  const bounds = L.latLngBounds();

  deliveryZones.forEach((zone) => {
    const circle = L.circle([zone.lat, zone.lng], {
      color: "#f97316", // orange-500
      fillColor: "#f97316",
      fillOpacity: 0.25,
      weight: 2,
      radius: zone.radius,
    }).addTo(map);

    // Adiciona tooltip com nome da zona
    if (zone.name) {
      circle.bindTooltip(zone.name, {
        permanent: false,
        direction: "center",
      });
    }

    deliveryZonesCircles.push(circle);

    // Expande bounds para incluir o círculo
    bounds.extend(circle.getBounds());
  });

  // Adiciona marcador do CEP pesquisado (se tiver coordenadas válidas)
  if (cepLat && cepLon) {
    // Ícone personalizado menor para o CEP
    const cepIcon = L.divIcon({
      html: `<div class="cep-marker-icon"></div>`,
      className: "cep-marker-container",
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    marker = L.marker([cepLat, cepLon], { icon: cepIcon }).addTo(map);

    if (address) {
      marker.bindPopup(`<strong>Seu endereço:</strong><br>${address}`);
    }

    // Inclui o marcador do CEP nos bounds
    bounds.extend([cepLat, cepLon]);
  }

  // Ajusta o mapa para mostrar todas as zonas
  if (bounds.isValid()) {
    map.fitBounds(bounds, {
      padding: [50, 50],
      maxZoom: 13,
    });
  }

  // Esconde loading
  if (loadingContainer) {
    loadingContainer.classList.add("hidden");
  }

  // Força recálculo do tamanho do mapa várias vezes (modal tem animação)
  const invalidateTimes = [100, 300, 500];
  invalidateTimes.forEach((time) => {
    setTimeout(() => {
      if (map) {
        map.invalidateSize();
        // Re-ajusta bounds após invalidateSize
        if (bounds.isValid()) {
          map.fitBounds(bounds, {
            padding: [50, 50],
            maxZoom: 13,
          });
        }
      }
    }, time);
  });

  // Listener para resize da janela
  window.addEventListener("resize", () => {
    if (map) {
      map.invalidateSize();
    }
  });
}

/**
 * Mostra resultado da consulta
 */
function showResult(type) {
  const resultContainer = document.getElementById("cep-result");
  const successEl = document.getElementById("cep-result-success");
  const errorEl = document.getElementById("cep-result-error");
  const actionButtons = document.getElementById("cep-action-buttons");
  const panelLoading = document.getElementById("cep-panel-loading");

  // Esconde loading do painel
  panelLoading?.classList.add("hidden");

  // Esconde todos
  [successEl, errorEl].forEach((el) => el?.classList.add("hidden"));
  actionButtons?.classList.add("hidden");
  actionButtons?.classList.remove("flex");

  // Mostra container
  resultContainer?.classList.remove("hidden");

  // Mostra o resultado correto
  switch (type) {
    case "success":
      successEl?.classList.remove("hidden");
      // Mostra botões apenas quando há entrega
      actionButtons?.classList.remove("hidden");
      actionButtons?.classList.add("flex");
      break;
    case "error":
      errorEl?.classList.remove("hidden");
      break;
  }
}

/**
 * Reseta o estado do modal
 */
function resetModal() {
  const resultEl = document.getElementById("cep-result");
  const loadingEl = document.getElementById("cep-map-loading");
  const panelLoading = document.getElementById("cep-panel-loading");
  const successEl = document.getElementById("cep-result-success");
  const errorEl = document.getElementById("cep-result-error");
  const actionButtons = document.getElementById("cep-action-buttons");
  const formContainer = document.getElementById("cep-interested-form-container");
  const interestedSuccess = document.getElementById("cep-interested-success");
  const form = document.getElementById("form-cep-interested");

  resultEl?.classList.add("hidden");
  loadingEl?.classList.remove("hidden");
  panelLoading?.classList.remove("hidden");
  [successEl, errorEl].forEach((el) => el?.classList.add("hidden"));
  actionButtons?.classList.add("hidden");
  actionButtons?.classList.remove("flex");

  // Reseta o formulário de interessados
  const interestedAlready = document.getElementById("cep-interested-already");
  formContainer?.classList.remove("hidden");
  interestedSuccess?.classList.add("hidden");
  interestedAlready?.classList.add("hidden");
  form?.reset();

  if (map) {
    map.remove();
    map = null;
    marker = null;
    deliveryZonesCircles = [];
  }
}

/**
 * Processa a busca de CEP
 */
async function processCepSearch(cep) {
  // Valida formato do CEP antes de tudo
  if (!isValidCep(cep)) {
    window.Toast?.error("CEP inválido", "Digite um CEP com 8 dígitos.");
    return;
  }

  // Busca dados do CEP ANTES de abrir o modal
  let cepData;
  try {
    cepData = await fetchCepData(cep);
    // Guarda dados do CEP para uso posterior (ex: formulário de interessados)
    lastCepData = { ...cepData, cep: cep.replace(/\D/g, "") };
  } catch {
    window.Toast?.error(
      "CEP não encontrado",
      "Verifique se digitou corretamente.",
    );
    return;
  }

  // CEP válido - agora abre o modal e carrega o mapa
  try {
    // Reseta modal
    resetModal();

    // Abre modal
    openModalById("#modal-cep-checker");

    // Carrega Leaflet
    const L = await loadLeaflet();

    // Monta endereço completo para o popup do mapa
    const fullAddress = [
      cepData.logradouro,
      cepData.bairro,
      cepData.localidade,
      cepData.uf,
    ]
      .filter(Boolean)
      .join(", ");

    // Busca coordenadas
    let coords;
    try {
      coords = await fetchCoordinates(
        cepData.logradouro || "",
        cepData.localidade,
        cepData.uf,
      );
    } catch {
      // Fallback: coordenadas genéricas da cidade/estado
      coords = { lat: -23.5505, lon: -46.6333 };
    }

    // Inicializa mapa com zonas de entrega
    await initMap(L, coords.lat, coords.lon, fullAddress);

    // Verifica zona de entrega
    try {
      const deliveryResult = await checkDeliveryZone(cep, cepData.uf);
      showResult(deliveryResult.delivers ? "success" : "error");
    } catch {
      // Em caso de erro no AJAX, considera que não entrega
      showResult("error");
    }
  } catch (error) {
    console.error("Erro ao processar CEP:", error);
    showResult("error");
    const loadingEl = document.getElementById("cep-map-loading");
    loadingEl?.classList.add("hidden");
  }
}

/**
 * Inicializa o CEP Checker
 */
export function initCepChecker() {
  // Seleciona o botão de busca na seção de entrega
  const searchButton = document.getElementById("btn-cep-search");
  const whereWeDeliverButton = document.getElementById("ondeEntrega");
  const cepInput = document.querySelector("#entrega .cep, .cep-home-search");
  const cartCepLookupForm = document.getElementById("cartCepLookupForm");
  const cartCepLookupInput = document.getElementById("cartCepLookupInput");

  const closeCartDrawerBeforeLookup = (callback) => {
    if (typeof window.closeCartDrawer === "function" && window.cartDrawerIsOpen) {
      window.closeCartDrawer();
      window.setTimeout(callback, 450);
      return;
    }

    callback();
  };

  const openCartCepModal = () => {
    openModalById("#modal-cart-cep-entry");

    window.setTimeout(() => {
      cartCepLookupInput?.focus();
    }, 150);
  };

  const handleCepLookup = async (cep) => {
    if (cep) {
      processCepSearch(cep);
      return;
    }

    openCartCepModal();
  };

  // Delegação no document: o botão #ondeEntrega vive dentro do cart-drawer,
  // que é re-renderizado via refreshCartDrawer() ao adicionar/remover itens.
  // Anexar direto no elemento perde o listener após o re-render.
  document.addEventListener("click", (e) => {
    const target = e.target.closest("#ondeEntrega");
    if (!target) return;
    e.preventDefault();
    const currentCep = cepInput?.value?.trim() || "";
    closeCartDrawerBeforeLookup(() => {
      handleCepLookup(currentCep);
    });
  });

  if (cartCepLookupInput) {
    cartCepLookupInput.addEventListener("input", () => {
      const digits = cartCepLookupInput.value.replace(/\D/g, "").slice(0, 8);
      cartCepLookupInput.value = digits.replace(/(\d{5})(\d{0,3})/, (match, first, second) => second ? `${first}-${second}` : first);
    });
  }

  if (cartCepLookupForm && cartCepLookupInput) {
    cartCepLookupForm.addEventListener("submit", (e) => {
      e.preventDefault();

      const cep = cartCepLookupInput.value.trim();
      if (!cep) {
        window.Toast?.warning(
          "Campo obrigatório",
          "Digite um CEP para consultar.",
        );
        cartCepLookupInput.focus();
        return;
      }

      closeModalById("#modal-cart-cep-entry");
      processCepSearch(cep);
    });
  }

  if (!searchButton || !cepInput) {
    // Tenta fallback para qualquer botão na seção de entrega
    const entregaSection = document.getElementById("entrega");
    if (entregaSection) {
      const fallbackBtn = entregaSection.querySelector(
        'button[aria-label="Buscar CEP"]',
      );
      const fallbackInput = entregaSection.querySelector("input.cep");

      if (fallbackBtn && fallbackInput) {
        fallbackBtn.addEventListener("click", (e) => {
          e.preventDefault();
          const cep = fallbackInput.value.trim();
          handleCepLookup(cep);
        });

        // Enter no input
        fallbackInput.addEventListener("keypress", (e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            fallbackBtn.click();
          }
        });
      }
    }
    return;
  }

  // Event listener no botão
  searchButton.addEventListener("click", (e) => {
    e.preventDefault();
    const cep = cepInput.value.trim();
    handleCepLookup(cep);
  });

  // Enter no input
  cepInput.addEventListener("keypress", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      searchButton.click();
    }
  });

  // Formulário de interessados (quando não há cobertura)
  initInterestedForm();
}

/**
 * Inicializa o formulário de interessados
 */
function initInterestedForm() {
  const form = document.getElementById("form-cep-interested");
  if (!form) return;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    const formContainer = document.getElementById("cep-interested-form-container");
    const successContainer = document.getElementById("cep-interested-success");
    const alreadyContainer = document.getElementById("cep-interested-already");
    const emailInput = form.querySelector('input[name="email"]');
    const submitBtn = form.querySelector('button[type="submit"]');
    const email = emailInput?.value?.trim();

    if (!email) {
      window.Toast?.warning("Campo obrigatório", "Digite seu e-mail.");
      return;
    }

    // Desabilita o botão durante o envio
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="text-sm text-white">Enviando...</span>';

    try {
      const result = await saveInterestedEmail(email);
      // Esconde o formulário
      formContainer?.classList.add("hidden");

      // Mostra mensagem apropriada
      if (result.status === "already_registered") {
        alreadyContainer?.classList.remove("hidden");
      } else {
        successContainer?.classList.remove("hidden");
      }
    } catch (error) {
      window.Toast?.error("Erro", error.message || "Tente novamente.");
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  });
}




